"use client";

import Image from "next/image";
import * as Lucide from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/lib/hooks/use-translation";
import { useLanguage } from "@/providers/LanguageProvider";
import { LOCALES, type Locale } from "@/lib/i18n/locale";

const FLAGS: Record<Locale, string> = {
  th: "/lang/TH.webp",
  en: "/lang/EN.webp",
};

/**
 * TH/EN switcher.
 *
 * It no longer owns the language — it used to read and write the cookie
 * itself and nothing on the page changed, because there was nothing to change.
 * Now the cookie is the proxy's input (lib/i18n/locale.ts) and the language is
 * baked into the server-rendered tree, so picking one is a matter of telling
 * the provider, which writes the cookie and asks the server for the page again
 * (see providers/LanguageProvider.tsx). That is why the labels here come
 * through `t()` like everything else: this component is rendered in whichever
 * language is current, same as the rest of the chrome.
 */
export default function LanguageSwitcher() {
  const { t } = useTranslation();
  const { language, setLanguage } = useLanguage();

  // The trigger needs a label because below `sm` it is a flag and a chevron
  // with no text at all. From `sm` the current language *is* visible, and an
  // accessible name that leaves it out breaks the match a voice-control user
  // relies on — saying "Eng" would not reach a button called only
  // "Change language". Leading with the visible word keeps that working and
  // still says what the button does.
  const current = t(`nav.language.${language}`);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          aria-label={`${current} — ${t("nav.language.label")}`}
          className="gap-1.5 px-2 font-medium text-gray-700"
        >
          <Image
            src={FLAGS[language]}
            alt=""
            width={22}
            height={16}
            className="h-4 w-auto object-contain"
          />
          <span className="hidden text-sm sm:inline">{current}</span>
          <Lucide.ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-36 border-[#E5E7EA]">
        {LOCALES.map((locale) => (
          <DropdownMenuItem
            key={locale}
            onClick={() => setLanguage(locale)}
            className={language === locale ? "bg-accent font-semibold" : ""}
          >
            <Image
              src={FLAGS[locale]}
              alt=""
              width={22}
              height={16}
              className="mr-2 h-4 w-auto object-contain"
            />
            {t(`nav.language.${locale}`)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
